/* eslint-disable jsx-a11y/anchor-has-content */
import React from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';

import lazyLoad from 'utils/lazyLoad';
import { userProfileProptype } from 'actions/loggedInUser';
import XMLink from 'components/XMLink';

import styles from './Header.module.scss';

// Simple invisible but non-null fallback is useful for both debugging and unit tests:
const makeFallback = name => <span data-async-load-failed={name} />;

const HeaderActions = lazyLoad(
  () =>
    import(/* webpackChunkName: "xMattersHeaderActions" */ './HeaderActions'),
  { fallback: makeFallback('HeaderActions') },
);
const NonProdBanner = lazyLoad(
  () => import(/* webpackChunkName: "NonProdBanner" */ './NonProdBanner'),
  { fallback: makeFallback('NonProdBanner') },
);
const RemainingVoiceCreditsBanner = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "RemainingVoiceCreditsBanner" */ './RemainingVoiceCreditsBanner'
    ),
  { fallback: makeFallback('RemainingVoiceCreditsBanner') },
);
const UserIntentBanner = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "UserIntentBanner" */ 'pages/ReactRoot/containers/UserIntentBanner'
    ),
  { fallback: makeFallback('UserIntentBanner') },
);

export default function Header({
  companyLogoUrl,
  companyName,
  helpId,
  homeLocation,
  alertsNavItem,
  menuId,
  showNonProdBanner,
  isReactLoginEnabled,
  user,
  voiceCredits,
  defaultXmLogoPath,
}) {
  return (
    <header className={styles.wrap}>
      <UserIntentBanner />
      {showNonProdBanner && (
        <NonProdBanner isReactLoginEnabled={isReactLoginEnabled} />
      )}

      {!isNil(voiceCredits) && (
        <RemainingVoiceCreditsBanner voiceCredits={voiceCredits} />
      )}

      <div className={styles.headerNav} data-testid="headerNav">
        <XMLink
          to={homeLocation}
          className={styles.logoWrap}
          data-testid="headerNav_logoLink"
        >
          <img
            src={companyLogoUrl || defaultXmLogoPath}
            alt="xMatters"
            className={styles.logo}
          />
        </XMLink>
        <HeaderActions
          alertsNavItem={alertsNavItem}
          companyName={companyName}
          helpId={helpId}
          menuId={menuId}
          user={user}
        />
      </div>
    </header>
  );
}

Header.displayName = 'Header';
Header.defaultProps = {
  companyLogoUrl: null,
  alertsNavItem: null,
};

Header.propTypes = {
  companyLogoUrl: PropTypes.string,
  companyName: PropTypes.string.isRequired,
  helpId: PropTypes.string,
  homeLocation: PropTypes.string.isRequired,
  alertsNavItem: PropTypes.shape({
    to: PropTypes.string.isRequired,
    icon: PropTypes.string,
    id: PropTypes.string.isRequired,
    textCode: PropTypes.string.isRequired,
  }),
  menuId: PropTypes.string.isRequired,
  showNonProdBanner: PropTypes.bool.isRequired,
  isReactLoginEnabled: PropTypes.bool.isRequired,
  user: userProfileProptype.isRequired,
};

Header.defaultProps = {
  helpId: '',
};
